import React, { useEffect, useState } from "react";

/**
 * BrandMark — the single source of truth for the N8One brand identity.
 *
 * Two PNG assets are shipped in /public/brand/:
 *   • n8one-wordmark.png — the full "N8One" wordmark (serif N + lemniscate 8 + italic script "One")
 *   • n8one-mark.png     — the icon-only mark (serif N + lemniscate 8) for inline usage & favicons
 *
 * All three exports (BrandMark, N8, LoadingEight) resolve to these two assets so
 * there is exactly ONE logo implementation across the whole app. The named
 * exports (BrandMark, InfinityEight, N8, LoadingEight) are preserved so every
 * existing import site keeps working without touching consumer files.
 */

const WORDMARK_SRC = "/brand/n8one-wordmark.png";
const MARK_SRC = "/brand/n8one-mark.png";

/**
 * InfinityEight — the compact mark. Renders as an inline image that scales
 * with the surrounding font-size (1em tall). Used inside body copy anywhere
 * the plain "N8" glyph would appear (kept as an export purely for backward
 * compatibility — <N8 /> below is the canonical inline mark).
 */
export const InfinityEight = ({ className = "", spinning = false, ...rest }) => (
  <img
    src={MARK_SRC}
    alt=""
    aria-hidden="true"
    draggable="false"
    className={`brand-mark inline-block align-[-0.16em] select-none ${spinning ? "brand-spinning" : ""} ${className}`}
    style={{ height: "1em", width: "auto" }}
    {...rest}
  />
);

/**
 * BrandMark — the full N8One wordmark, rendered from the PNG asset.
 * Height scales with the surrounding font-size so it lives naturally
 * inside any typography scale (header, onboarding, footer). A single
 * gentle fade-and-settle plays on first mount (once per session).
 *
 * Keeps `data-testid` API and the `signOnMount` / `idleWhisper` props
 * so no consumer needs to change — extra props are simply ignored when
 * they don't apply to the PNG.
 */
export const BrandMark = ({
  className = "",
  testid = "brand-wordmark",
  signOnMount = true,
  // Kept for backward compat; the PNG wordmark doesn't whisper.
  // eslint-disable-next-line no-unused-vars
  idleWhisper = true,
}) => {
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    if (!signOnMount) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    let played = false;
    try {
      played = sessionStorage.getItem("brand-signature-played") === "1";
    } catch (e) {
      played = false;
    }
    if (played) return;

    const startId = setTimeout(() => setEntering(true), 120);
    const endId = setTimeout(() => {
      setEntering(false);
      try {
        sessionStorage.setItem("brand-signature-played", "1");
      } catch (e) {
        /* noop */
      }
    }, 120 + 1800);
    return () => {
      clearTimeout(startId);
      clearTimeout(endId);
    };
  }, [signOnMount]);

  return (
    <span
      className={`brand-wordmark inline-flex items-center leading-none ${entering ? "brand-entering" : ""} ${className}`}
      data-testid={testid}
    >
      <img
        src={WORDMARK_SRC}
        alt="N8One"
        draggable="false"
        className="brand-wordmark-img select-none"
        style={{ height: "1.75em", width: "auto", display: "block" }}
      />
      <span className="sr-only">N8One</span>
    </span>
  );
};

/**
 * N8 — canonical inline mark for phrases like "N8 Guide", "N8 Pro".
 * Renders the icon-only PNG at ~1em height, baseline-aligned so it
 * reads as part of the surrounding sentence.
 */
export const N8 = ({ className = "", testid }) => (
  <img
    src={MARK_SRC}
    alt="N8"
    draggable="false"
    data-testid={testid}
    className={`brand-mark-inline inline-block align-[-0.16em] select-none ${className}`}
    style={{ height: "1em", width: "auto" }}
  />
);

/**
 * LoadingEight — on-brand inline spinner using the icon-only PNG.
 * The gentle breath animation is defined in index.css (.brand-spinning).
 */
export const LoadingEight = ({ className = "", label = null, testid = "loading-eight" }) => (
  <span
    className="inline-flex items-center gap-2"
    data-testid={testid}
    role="status"
    aria-live="polite"
  >
    <img
      src={MARK_SRC}
      alt=""
      aria-hidden="true"
      draggable="false"
      className={`brand-mark brand-spinning inline-block align-[-0.16em] select-none ${className}`}
      style={{ height: "1em", width: "auto" }}
    />
    {label && <span className="text-sm text-[hsl(var(--muted-foreground))]">{label}</span>}
    <span className="sr-only">Loading</span>
  </span>
);

export default BrandMark;
